import React, { useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidatingInput } from '@/components/forms/validation';
import { ChangedItem } from '@/types';
import { Button } from '@ygtechnologies/rubus-ui';
import { faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { EditorFeeConfiguration } from '.';
import theme from './FeeEditorItem.module.scss';
import { classnames } from '@/utils/html';
import { InputWrapper } from '@ygtechnologies/bygr-frontend-component-library';
import { filterNumericInput, getNumberValue } from '@/utils/string';

interface FeeEditorItemProps {
    value: EditorFeeConfiguration;
    onChange(change: ChangedItem<EditorFeeConfiguration>): void;
    onDeleteClick(item: EditorFeeConfiguration): void;
}

const FeeEditorItem: React.FC<FeeEditorItemProps> = ({ value, onChange, onDeleteClick }) => {
    const [feeInputValue, setFeeInputValue] = useState(value.feePercentage.toString());

    useEffect(() => {
        // Only re-sync when the prop differs numerically; otherwise an in-progress
        // "7." (== 7) would get rewritten to "7", eating the decimal point.
        if (getNumberValue(feeInputValue) !== value.feePercentage) {
            setFeeInputValue(value.feePercentage.toString());
        }
    }, [value.feePercentage]);

    const triggerChange = (changes: Partial<EditorFeeConfiguration>) => onChange(ChangedItem.fromMerge(value, changes));

    return (
        <div className={theme.container}>
            <div className={classnames('form-group', theme.name)}>
                <ValidatingInput
                    className="form-control no-spinner"
                    type="text"
                    value={value.name}
                    placeholder={t`Fee name`}
                    onChange={(e) => triggerChange({ name: e.target.value })}
                />
                <Button variant="danger" size="sm" icon={faTrashAlt} onClick={() => onDeleteClick(value)} />
            </div>

            <label>
                <Trans>Fee</Trans>
                <InputWrapper appendContent="%">
                    <ValidatingInput
                        className="form-control no-spinner"
                        type="text"
                        value={feeInputValue}
                        onChange={(e) => {
                            const filteredValue = filterNumericInput(e.target.value);
                            setFeeInputValue(filteredValue);
                            triggerChange({ feePercentage: getNumberValue(filteredValue) });
                        }}
                    />
                </InputWrapper>
            </label>
        </div>
    );
};

export default FeeEditorItem;
